// ROUTING PRINCIPLE: every state a user can be looking at (tab, log channel, filter, job,
// login step) lives in the URL, not component state — path params carry navigation, search
// params carry view state. Secrets (credentials, raw keys, TOTP pending token) stay in memory, never in the URL.

import {
  createRootRoute,
  createRoute,
  createRouter,
  createMemoryHistory,
  Outlet,
} from '@tanstack/react-router';
import { LoginRoute } from '../features/auth/LoginRoute';
import { AuthedLayout } from './AuthedLayout';
import { FleetRoute } from '../features/fleet/FleetRoute';
import { isFleetFilter, type FleetFilter } from '../features/fleet/FleetList';
import { UpdatesRoute } from '../features/updates/UpdatesRoute';
import { KeysRoute } from '../features/keys/KeysRoute';
import { IntegrationsRoute } from '../features/integrations/IntegrationsRoute';
import { SiteDrilldown } from '../features/site/SiteDrilldown';
import { NotFoundRoute } from './NotFoundRoute';

const rootRoute = createRootRoute({
  component: () => <Outlet />,
  // A mistyped URL gets a real screen with a way back, not a blank page.
  notFoundComponent: NotFoundRoute,
});

// `redirect` round-trips the URL an anon operator was trying to reach, so signing in
// returns them there instead of always dumping them on the fleet.
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginRoute,
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => {
    const redirect = search.redirect;
    // Only same-origin PATHS: an absolute URL here would be an open redirect.
    return typeof redirect === 'string' && redirect.startsWith('/') && !redirect.startsWith('//')
      ? { redirect }
      : {};
  },
});

const authedLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authed',
  component: AuthedLayout,
});

const fleetRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/',
  component: FleetRoute,
  validateSearch: (search: Record<string, unknown>): { filter?: FleetFilter } =>
    isFleetFilter(search.filter) ? { filter: search.filter } : {},
});

const updatesRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/updates',
  component: UpdatesRoute,
  validateSearch: (search: Record<string, unknown>): { job?: string } =>
    typeof search.job === 'string' && search.job ? { job: search.job } : {},
});

// Job + session are per-tab but live on the same route, so one validator serves every tab segment.
function validateSiteSearch(
  search: Record<string, unknown>,
): { job?: string; session?: string } {
  const out: { job?: string; session?: string } = {};
  if (typeof search.job === 'string' && search.job) out.job = search.job;
  if (typeof search.session === 'string' && search.session) out.session = search.session;
  return out;
}

/** Bare /sites/{id} keeps old links working by landing on Status. */
const siteRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/sites/$siteId',
  component: SiteDrilldown,
  validateSearch: validateSiteSearch,
});

/** /sites/{id}/{tab} — the tab an operator is on is part of the address. */
const siteTabRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/sites/$siteId/$tab',
  component: SiteDrilldown,
  validateSearch: validateSiteSearch,
});

/** /sites/{id}/logs/{channel} — the log tail being read is addressable too. */
const siteTabChannelRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/sites/$siteId/$tab/$channel',
  component: SiteDrilldown,
});

const keysRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/keys',
  component: KeysRoute,
  // Surviving a refresh is deliberate — the operator sees what they were about to do. The RAW key is never here.
  validateSearch: (search: Record<string, unknown>): { confirmRevoke?: string } =>
    typeof search.confirmRevoke === 'string' && search.confirmRevoke
      ? { confirmRevoke: search.confirmRevoke }
      : {},
});

const integrationsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/integrations',
  component: IntegrationsRoute,
  validateSearch: (search: Record<string, unknown>): { confirmRemove?: string } =>
    typeof search.confirmRemove === 'string' && search.confirmRemove
      ? { confirmRemove: search.confirmRemove }
      : {},
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  authedLayoutRoute.addChildren([
    fleetRoute,
    updatesRoute,
    siteRoute,
    siteTabRoute,
    siteTabChannelRoute,
    keysRoute,
    integrationsRoute,
  ]),
]);

export interface AppRouterOptions {
  history?: 'memory' | 'browser';
  initialEntries?: string[];
}

export function createAppRouter(opts: AppRouterOptions = {}) {
  const history =
    opts.history === 'memory'
      ? createMemoryHistory({ initialEntries: opts.initialEntries ?? ['/'] })
      : undefined;

  return createRouter({
    routeTree,
    history,
    defaultPreload: 'intent',
  });
}

export type AppRouter = ReturnType<typeof createAppRouter>;

// Register the router type for typed navigation/links.
declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
}
